const POPUP_WINDOW_FEATURES =
  'popup=yes,width=1440,height=900,menubar=yes,toolbar=yes,location=yes,resizable=yes,scrollbars=yes';

export const RESOLVE_SYNC_WINDOW_NAME = 'ecam-resolve';
export const EVENTQ_SYNC_WINDOW_NAME = 'ecam-eventq';

const siblingWindowRefs = new Map<string, Window | null>();

export interface OpenSiblingAppOptions {
  /** When true, open a sized popup window; when false, a normal browser tab (same window). */
  popout?: boolean;
  /**
   * When false, open (or reuse) the sibling without taking focus from this tab.
   * Default true — Sync CTA should bring the sibling forward.
   */
  stealFocus?: boolean;
}

/**
 * Path prefix from a configured SPA base (`http://localhost:5050/resolve` → `/resolve`).
 * Empty config falls back so local/staging placeholders still resolve same-origin.
 */
export function siblingAppPathPrefix(configuredBaseUrl: string, fallbackPrefix: string): string {
  const trimmed = configuredBaseUrl.trim().replace(/\/$/, '');
  const fallback = fallbackPrefix.startsWith('/') ? fallbackPrefix : `/${fallbackPrefix}`;
  if (!trimmed) {
    return fallback.replace(/\/$/, '') || fallback;
  }

  try {
    if (/^https?:\/\//i.test(trimmed)) {
      const pathname = new URL(trimmed).pathname.replace(/\/$/, '');
      return pathname || fallback.replace(/\/$/, '') || '/';
    }
  } catch {
    // Use as a path below.
  }

  return (trimmed.startsWith('/') ? trimmed : `/${trimmed}`).replace(/\/$/, '');
}

/**
 * Host the sibling SPA on this window's origin (YARP or hosted web app).
 * Path prefix comes from config; the host is never hardcoded.
 */
export function siblingAppOrigin(_configuredBaseUrl: string): string {
  return globalThis.location.origin;
}

/**
 * Sibling Agent UI URL on the current host + configured path prefix.
 */
export function siblingAppHref(
  configuredBaseUrl: string,
  inAppPath: string,
  fallbackPrefix: string,
): string {
  const prefix = siblingAppPathPrefix(configuredBaseUrl, fallbackPrefix);
  const path = inAppPath.startsWith('/') ? inAppPath : `/${inAppPath}`;
  return `${siblingAppOrigin(configuredBaseUrl)}${prefix}${path}`;
}

export function isSiblingAppWindowOpen(windowName: string): boolean {
  const existing = siblingWindowRefs.get(windowName);
  return !!existing && !existing.closed;
}

/**
 * Focus a tracked sibling window opened from this tab.
 * Does not call `window.open('', name)` — that creates blank tabs when no named window exists.
 */
export function focusSiblingAppWindow(windowName: string): boolean {
  const tracked = siblingWindowRefs.get(windowName);
  if (tracked && !tracked.closed && tracked !== window) {
    tracked.focus();
    return true;
  }

  return false;
}

/**
 * Brings this Agent UI browser tab to the front when it receives a cross-app sync.
 */
export function focusThisBrowserTab(): void {
  if (!document.hasFocus()) {
    window.focus();
  }
}

/**
 * Marks this browsing context as the sync target for the sibling app so
 * `window.open(url, windowName)` from the other Agent UI reuses this tab.
 */
export function claimSiblingAppWindowName(windowName: string): void {
  // Always claim the sync name so Sync never opens a second EventQ/Resolve instance.
  window.name = windowName;
  siblingWindowRefs.set(windowName, window);
}

/** Opens (or focuses) the sibling app. Default is a normal tab in this browser. */
export function openSiblingAppWindow(
  url: string,
  windowName: string,
  options: OpenSiblingAppOptions = {},
): Window | null {
  const popout = options.popout === true;
  const stealFocus = options.stealFocus !== false;
  const tracked = siblingWindowRefs.get(windowName);

  if (tracked && !tracked.closed && tracked !== window) {
    if (stealFocus) {
      tracked.focus();
    }
    try {
      if (tracked.location.href !== url) {
        tracked.location.assign(url);
      }
      siblingWindowRefs.set(windowName, tracked);
      return tracked;
    } catch {
      // Cross-origin — fall through to named window.open (browser may still reuse).
    }
  }

  // Named target: browser reuses an existing tab/window with this name when present.
  const opened = popout
    ? window.open(url, windowName, POPUP_WINDOW_FEATURES)
    : window.open(url, windowName);

  if (!opened || opened.closed) {
    return null;
  }

  siblingWindowRefs.set(windowName, opened);

  if (opened !== window) {
    if (stealFocus) {
      opened.focus();
    } else {
      window.focus();
    }
  }

  try {
    opened.opener = null;
  } catch {
    // Cross-origin / browser policy — window still opened on this origin.
  }

  return opened;
}

/** Test-only reset of tracked sibling window references. */
export function resetSiblingAppWindowRefsForTests(): void {
  siblingWindowRefs.clear();
}
